import { useGetList, useGetOne, useRecordContext, useTranslate } from "ra-core";
import { Link } from "react-router";

import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { Card, CardContent } from "@/components/ui/card";

import { AddTask } from "../tasks/AddTask";
import { TasksIterator } from "../tasks/TasksIterator";
import { CompanyAvatar } from "../companies/CompanyAvatar";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { findDealLabel } from "../deals/dealUtils";
import type { Company, Contact, Deal } from "../types";
import { ContactJourney } from "./ContactJourney";

/**
 * Right associations column of the contact record - the linked company,
 * deals this contact is on, tasks, and the attribution journey.
 */

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium mb-2">
    {children}
  </h2>
);

export const ContactAssociations = () => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const { dealStages, currency } = useConfigurationContext();

  const { data: company } = useGetOne<Company>(
    "companies",
    { id: record?.company_id ?? undefined },
    { enabled: record?.company_id != null },
  );
  const { data: allDeals = [] } = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 1000 },
  });

  if (!record) return null;

  const deals = allDeals.filter(
    (d) =>
      !d.archived_at &&
      (d.contact_ids?.includes(record.id) ||
        (record.company_id != null && d.company_id === record.company_id)),
  );

  const fmt = (n: number) =>
    n.toLocaleString("en-US", {
      notation: "compact",
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 1,
    });

  return (
    <div className="hidden xl:flex flex-col gap-4 text-sm">
      {company && (
        <Card>
          <CardContent className="px-4">
            <SectionTitle>Company</SectionTitle>
            <Link
              to={`/companies/${company.id}/show`}
              className="flex items-center gap-3 group"
            >
              <CompanyAvatar record={company} />
              <div className="min-w-0">
                <p className="font-medium group-hover:underline truncate">
                  {company.name}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {company.sector}
                  {company.city ? ` · ${company.city}` : ""}
                </p>
              </div>
            </Link>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="px-4">
          <SectionTitle>
            Deals{deals.length ? ` · ${deals.length}` : ""}
          </SectionTitle>
          {deals.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No open deals with this contact yet.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {deals.slice(0, 5).map((deal) => (
                <Link
                  key={deal.id}
                  to={`/deals/${deal.id}/show`}
                  className="group"
                >
                  <p className="font-medium group-hover:underline truncate">
                    {deal.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {fmt(deal.amount)} ·{" "}
                    {findDealLabel(dealStages, deal.stage) ?? deal.stage}
                  </p>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="px-4">
          <SectionTitle>
            {translate("resources.tasks.name", { smart_count: 2 })}
          </SectionTitle>
          <div className="max-h-64 overflow-y-auto">
            <ReferenceManyField
              target="contact_id"
              reference="tasks"
              sort={{ field: "due_date", order: "ASC" }}
              perPage={1000}
            >
              <TasksIterator />
            </ReferenceManyField>
          </div>
          <AddTask />
        </CardContent>
      </Card>

      {(record.touches?.length ?? 0) > 0 && (
        <Card>
          <CardContent className="px-4">
            <ContactJourney />
          </CardContent>
        </Card>
      )}
    </div>
  );
};
